import type { Item, Level, Vec2 } from '../hp/types';

/** A base this flat is a rug or a doormat: it travels alone instead of dragging the room with it. */
const MIN_BASE_HEIGHT = .08;
/** How far an item's bottom may sit from the base's top plane and still count as standing on it. */
const CONTACT = .04;
/** A plate may overhang the edge by this much and still travel with the table. */
const OVERHANG = .02;

export interface StackTransform { position: Vec2; rotation: number; elevation?: number }
export type PlacedTransform = Required<StackTransform>;
/** The height, in metres above the floor, of the highest loaded surface of `base` under any of
 * `points`, or null when none of them is over its meshes. The renderer supplies one from the
 * loaded models (`src/render/three/surfaceProbe.ts`); without it the box top stands in. */
export interface SurfaceLayer {
  bottom: number;
  top: number;
  /** The nearest underside above this surface, across the sampled footprint. */
  ceiling: number;
  /** Every sample rests on this layer; interior shelves require the whole footprint to fit. */
  supported: boolean;
  /** Closed cabinet facades block entering their interior, but existing contents still travel. */
  accessible: boolean;
}
export type SurfaceProbe = ((base: Item, points: readonly Vec2[]) => number | null) & {
  /** All loaded horizontal layers; null means unavailable geometry, [] means no surface here. */
  layers?: (base: Item, points: readonly Vec2[]) => SurfaceLayer[] | null;
};

/**
 * Items standing on `base`: bottom on its top plane (±4 cm) with their centre inside its outline.
 * They move and turn with the base but stay independent objects.
 *
 * Ownership is by the nearest surface, as in the native editor: a cup on a tray on a table sits on
 * the tray's top plane, not the table's, so moving the table takes the tray and the tray takes the cup.
 */
export function stackedItems(items: readonly Item[], base: Item, probe?: SurfaceProbe): Item[] {
  if (base.placement !== 'floor' || base.size.h < MIN_BASE_HEIGHT) return [];
  return items.filter(item => item.placement === 'floor' && standsOn(item, base, probe));
}

/** The height of an item's bottom and top faces, whichever point its elevation measures. */
export function verticalExtent(item: Item): { bottom: number; top: number } {
  const bottom = item.pivot === 'center' ? item.elevation - item.size.h / 2 : item.elevation;
  return { bottom, top: bottom + item.size.h };
}

/**
 * Whether `item` stands on `base`, whatever the base hangs on: bottom on the base's surface under
 * the item's centre (±4 cm) with that centre inside the base's outline (+2 cm). The surface is the
 * loaded mesh when a `probe` can read it — a pillow rests on a chair's seat, well below the top of
 * its back — and the box top otherwise. The rug rule lives in `stackedItems`; a wall shelf is
 * thinner than a rug yet carries what stands on it.
 */
export function standsOn(item: Item, base: Item, probe?: SurfaceProbe): boolean {
  if (item.id === base.id) return false;
  const c = Math.cos(base.rotation), s = Math.sin(base.rotation);
  const dx = item.position.x - base.position.x, dz = item.position.z - base.position.z;
  if (Math.abs(dx * c + dz * s) > base.size.w / 2 + OVERHANG || Math.abs(-dx * s + dz * c) > base.size.d / 2 + OVERHANG) return false;
  const bottom = verticalExtent(item).bottom;
  const layers = probe?.layers?.(base, [item.position]);
  if (layers !== null && layers !== undefined) return layers.some(layer => Math.abs(bottom - layer.top) <= CONTACT);
  const surface = probe?.(base, [item.position]) ?? verticalExtent(base).top;
  return Math.abs(bottom - surface) <= CONTACT;
}

/** Where everything standing on `base` lands once the base itself becomes `transform`. */
export function stackTransforms(items: readonly Item[], base: Item, transform: StackTransform, probe?: SurfaceProbe): Map<string, PlacedTransform> {
  const moved = new Map<string, PlacedTransform>();
  const passengers = stackedItems(items, base, probe);
  if (!passengers.length) return moved;
  const delta = transform.rotation - base.rotation, c = Math.cos(delta), s = Math.sin(delta);
  const lift = (transform.elevation ?? base.elevation) - base.elevation;
  for (const item of passengers) {
    const dx = item.position.x - base.position.x, dz = item.position.z - base.position.z;
    moved.set(item.id, { position: { x: transform.position.x + dx * c - dz * s, z: transform.position.z + dx * s + dz * c },
      rotation: item.rotation + delta, elevation: item.elevation + lift });
  }
  return moved;
}

/**
 * Moves, turns or raises `id` and carries whatever stands on it. `patch` holds any further fields the
 * base itself gains — the wall a wall-mounted item lands on. The level is returned unchanged when the
 * transform changes nothing, and items that do not travel keep their identity.
 */
export function transformItemWithStack(level: Level, id: string, transform: StackTransform, patch?: Partial<Item>, probe?: SurfaceProbe): Level {
  const source = level.items.find(item => item.id === id);
  if (!source) return level;
  const elevation = transform.elevation ?? source.elevation;
  const settled = source.position.x === transform.position.x && source.position.z === transform.position.z
    && source.rotation === transform.rotation && elevation === source.elevation;
  if (settled && !patch) return level;
  const moved = stackTransforms(level.items, source, { ...transform, elevation }, probe);
  if (settled && !moved.size && patch && Object.entries(patch).every(([key, value]) => source[key as keyof Item] === value)) return level;
  const target: Item = { ...source, ...patch, position: { x: transform.position.x, z: transform.position.z },
    rotation: transform.rotation, elevation };
  return { ...level, items: level.items.map(item => {
    if (item.id === id) return target;
    const next = moved.get(item.id);
    return next ? { ...item, position: { ...next.position }, rotation: next.rotation, elevation: next.elevation } : item;
  }) };
}
